import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { NbCalendarRange } from '@nebular/theme';
import { filterSuccessResult, injectQuery } from '@ngneat/query';
import { TranslateService } from '@ngx-translate/core';
import {
  IReservationValidateData,
  IValidReservationData,
  IReservationsParams,
  EReservationStatus,
  IReservationsGroup,
  IBookingSchedule,
  IBookingSettings,
  IMapWorkstation,
  EOfficeAddon,
  IReservation,
  IMapResponse,
  IMapZoneArea,
  IMapPayload,
  IApiPage,
  IOffice,
} from '@senet-id/core/types';
import { BehaviorSubject, catchError, EMPTY, filter, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { dialog, fromQuery } from '@senet-uiverse/common';
import { loadConfirmationDialog, ToastService } from 'ui';
import { WebsocketDispatcherService } from '@senet-uiverse/websockets';

import { ECounterType, EWebsocket } from '../enums';

import { OrganizationService } from './organization.service';
import { StorageService } from './storage.service';

export interface IBookingFilters {
  period: NbCalendarRange<Date | null>;
  office: IOffice;
}

export interface IBookingWidgetState {
  hasBooking: boolean;
  isLoading: boolean;
}

export interface IBookingSocketData {
  fromDate: string;
  officeId: number;
  toDate: string;
}

const DEFAULT_WIDGET_DATA: IBookingWidgetState = { isLoading: true, hasBooking: false };

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  private readonly organizationService = inject(OrganizationService);
  private readonly wsDispatcher = inject(WebsocketDispatcherService);
  private readonly translateService = inject(TranslateService);
  private readonly storageService = inject(StorageService);
  private readonly toastService = inject(ToastService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly query = injectQuery();
  private readonly dialog = dialog();

  readonly promoCode$ = new BehaviorSubject<string | null>(null);
  readonly widgetState = signal<IBookingWidgetState>(DEFAULT_WIDGET_DATA);
  readonly bookingOffices = toSignal(this.getBookingOffices(), { initialValue: [] });
  readonly officeSettings = signal<IBookingSettings | null>(null);
  readonly selectedFilters = signal<IBookingFilters | null>(null);
  readonly selectedFilters$ = toObservable(this.selectedFilters);
  readonly reservationsOffice = signal<number | null>(null);
  readonly reservationsGroups = signal<IReservationsGroup[]>([]);
  readonly mapElements = signal<IMapWorkstation[]>([]);
  readonly mapZoneAreas = signal<IMapZoneArea[]>([]);
  readonly zoneAreasMap = new Map<string, IMapZoneArea>();
  readonly selectedSet = new Set<number>([]);

  getBookingOffices() {
    return this.organizationService.currentOrganization$.asObservable().pipe(
      switchMap((organization) => {
        if (!organization) return of([]);

        return this.getFilteredOffices();
      }),
    );
  }

  subscribeToWidgetState() {
    return this.organizationService.currentOrganization$.asObservable().pipe(
      filter(Boolean),
      switchMap(() => {
        this.widgetState.set(DEFAULT_WIDGET_DATA);

        return this.getFilteredOffices();
      }),
      tap((offices) => this.widgetState.set({ isLoading: false, hasBooking: !!offices.length })),
      finalize(() => this.widgetState.set(DEFAULT_WIDGET_DATA)),
    );
  }

  getSettingsList() {
    const organization = this.storageService.storage().organization;

    return this.query({
      queryKey: ['booking', 'settings', organization] as const,
      queryFn: () => {
        return this.http.get<IBookingSettings[]>(`booking/settings/${organization}`);
      },
    }).result$.pipe(
      filterSuccessResult(),
      map(({ data }) => data),
    );
  }

  getOfficeSchedule(officeId: number) {
    const organization = this.storageService.storage().organization;

    return fromQuery(
      this.query({
        queryKey: ['booking', 'schedule', organization, officeId] as const,
        queryFn: () => {
          return this.http.get<IBookingSchedule[]>(`booking/schedule-settings/${organization}/${officeId}`);
        },
      }).result$,
    ) as Observable<IBookingSchedule[]>;
  }

  openBooking(route: string) {
    if (this.widgetState().hasBooking) {
      this.router.navigate([route]);
    } else {
      this.sendClick();
      this.openUnavailableDialog();
    }
  }

  clearData() {
    this.selectedFilters.set(null);
    this.officeSettings.set(null);
    this.promoCode$.next(null);
    this.clearMapElements();
  }

  clearMapElements() {
    this.mapZoneAreas.set([]);
    this.mapElements.set([]);
    this.selectedSet.clear();
    this.zoneAreasMap.clear();
  }

  getMap(payload: IMapPayload) {
    const organizationId = this.storageService.storage().organization;
    const { office_id: officeId, ...params } = payload;

    return this.http.get<IMapResponse>(`booking/map/${organizationId}/${officeId}`, { params }).pipe(
      map((response) => {
        return <IMapResponse>{
          booking_data: response.booking_data.map((element) => {
            if (!element.is_available && this.selectedSet.has(element.id)) {
              this.selectedSet.delete(element.id);
            }

            return {
              ...element,
              grid_area: this.createGridArea(element),
              is_selected: this.selectedSet.has(element.id),
            };
          }),
          map_squares: response.map_squares.map((element) => {
            this.zoneAreasMap.set(`${element.map_x}-${element.map_y}`, element);

            return {
              ...element,
              grid_area: this.createGridArea(element),
            };
          }),
        };
      }),
      tap((response) => {
        this.mapElements.set(response.booking_data);
        this.mapZoneAreas.set(response.map_squares);
      }),
    );
  }

  toggleMapElement(workstationId: number) {
    this.mapElements.update((elements) => {
      const newElements = [...elements];
      const idx = newElements.findIndex((el) => el.id === workstationId);

      if (idx === -1) return newElements;

      const newState = !newElements[idx].is_selected;

      if (newState && !this.canSelect()) {
        this.toastService.danger({
          message: this.translateService.instant('notifications.booking.devices-limit.error'),
        });

        return newElements;
      }

      if (newState) {
        this.selectedSet.add(workstationId);
      } else {
        this.selectedSet.delete(workstationId);
      }

      newElements[idx] = {
        ...newElements[idx],
        is_selected: newState,
      };

      return newElements;
    });
  }

  validateReservation(data: IReservationValidateData) {
    const organizationId = this.storageService.storage().organization;

    return this.http.post<IValidReservationData>(`booking/validate/${organizationId}`, data);
  }

  createReservation(data: IReservationValidateData) {
    const organizationId = this.storageService.storage().organization;

    return this.http.post<IValidReservationData>(`booking/submit/${organizationId}`, data);
  }

  cancelReservation(reservationId: number) {
    const organizationId = this.storageService.storage().organization;

    return this.http.post<void>(`booking/cancel/${organizationId}/${reservationId}`, {});
  }

  updateReservationNote(data: { reservationId: number; description: string | null }) {
    const organizationId = this.storageService.storage().organization;
    const { reservationId, description } = data;

    return this.http.patch<void>(`booking/edit/${organizationId}/${reservationId}`, { description });
  }

  getReservations(filters: IReservationsParams = {}) {
    const params = new HttpParams({ fromObject: filters as Record<string, string | number | boolean> });
    const organizationId = this.storageService.storage().organization;

    return this.http.get<IApiPage<IReservation>>(`booking/${organizationId}`, { params });
  }

  getUpcomingReservation() {
    return this.getReservations({ status: EReservationStatus.Active }).pipe(
      map((response) => {
        const items = response.items;

        if (items.length) {
          return items[items.length - 1];
        }

        return null;
      }),
    );
  }

  updateReservation({ groupIdx, reservation }: { groupIdx: number; reservation: IReservation }) {
    const reservationIdx = this.reservationsGroups()[groupIdx]?.reservations?.findIndex(
      (item) => item.id === reservation.id,
    );

    if (reservationIdx === -1 || reservationIdx === undefined) return;

    const groups = [...this.reservationsGroups()];

    groups[groupIdx].reservations[reservationIdx] = reservation;

    this.reservationsGroups.set(groups);
  }

  bookingSubscriptions(data: Readonly<IBookingSocketData>) {
    return this.wsDispatcher.bindToChannel(
      `${EWebsocket.WORKSTATION_AVAILABILITY}:all`,
      EWebsocket.WORKSTATION_AVAILABILITY,
      {
        params: {
          office_id: data.officeId,
          from_date: data.fromDate,
          to_date: data.toDate,
        },
        token: this.storageService.storage().user?.keycloak_access_token,
      },
    );
  }

  private sendClick() {
    this.organizationService
      .incrementCount(ECounterType.BookingWidget)
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  private openUnavailableDialog() {
    this.dialog.open({
      component: loadConfirmationDialog(),
      config: {
        context: {
          titleText: this.translateService.instant('dialogs.unavailable-booking.title'),
          text: this.translateService.instant('dialogs.unavailable-booking.text'),
          confirmBtn: this.translateService.instant('common.buttons.got-it'),
          imageUrl: '/images/robots/question.svg',
          closeBtn: false,
        },
      },
    });
  }

  /**
   * @description Returns offices with enabled booking addon and settings allowing booking via Senet ID
   * @returns {Observable<IOffice[]>}
   */
  private getFilteredOffices(): Observable<IOffice[]> {
    return this.organizationService.getOfficeList().pipe(
      map((offices) => {
        return offices.filter((office) => {
          return office.addons?.some((addon) => {
            return addon.addon_id === EOfficeAddon.Booking && addon.is_enabled;
          });
        });
      }),
      switchMap((offices) => {
        if (offices.length === 0) {
          return of([]);
        }

        return this.getSettingsList().pipe(
          map((settings) => {
            return offices.filter((office) => {
              return settings.some((setting) => setting.dic_office === office.id && setting.allow_booking_via_senet_id);
            });
          }),
        );
      }),
    );
  }

  private canSelect() {
    const settings = this.officeSettings();
    const selectedCount = this.selectedSet.size;

    if (selectedCount === 0) return true;

    const maxCount = settings?.group_reservation_enabled ? (settings?.devices_max_quantity ?? 1) : 1;

    return maxCount === 0 ? true : maxCount > selectedCount;
  }

  private createGridArea(element: { map_x: number; map_y: number }) {
    const col = element.map_x + 1;
    const row = element.map_y + 1;

    return `${row} / ${col} / ${row + 1} / ${col + 1}`;
  }
}
